import { ReactNode } from 'react'
// next
import { useRouter } from 'next/router'
// hooks
import useAuth from 'hooks/useAuth'
import useSession from 'hooks/commons/useSession'
import Login from '~/pages/auth/login'
import Page403 from '~/pages/403'
import Page404 from '~/pages/404'
// components
import LoadingScreen from 'components/LoadingScreen'
import { IsAccessible } from 'helpers/user-access'
import { isRetiredRoute, resolveRouteAccess } from 'helpers/access'
import { PATH_AUTH } from 'routes/paths'

type Props = {
  children: ReactNode
}

export default function AuthGuard({ children }: Props) {
  const { isAuthenticated, isInitialized } = useAuth()
  const { pathname, replace } = useRouter()
  const { permissions, roles, isLoadingPermissions, isMultipleRolesEnabled } =
    useSession()

  // Gates the route itself: the legacy roles whitelist, or the /me granted
  // actions once the multiple-roles flag is on. IsAccessible is hook-shaped
  // (it calls useRouter), so it runs here and its result is handed to the
  // pure decider.
  const { isAllowed, isLoading } = resolveRouteAccess({
    pathname,
    permissions,
    isLoadingPermissions,
    isMultipleRolesEnabled,
    legacyIsAllowed: Boolean(IsAccessible(roles)),
  })

  // Before anything auth-related: a retired feature is gone for everyone, so
  // this must not depend on who is asking or on permissions having loaded.
  // 404 rather than 403 — the page no longer exists, and a permission screen
  // would suggest someone could be granted access to it.
  if (isRetiredRoute(pathname)) {
    return <Page404 />
  }

  if (!isInitialized) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    replace(PATH_AUTH.login)
    return <Login />
  }

  // Still resolving the permissions this route is gated on — don't render the
  // page only to replace it with a 403 a tick later.
  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAllowed) {
    return <Page403 />
  }

  return <>{children}</>
}
